import { useEmailMessageContentMedia } from '@Core/api/EmailMessageApi';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';

type EmailMessageSearchData = Diagonal.Notifications.Data.EmailMessageSearchData;

/**
 * The email-history index filters plus the
 * legacy `filter[status]` and pagination. Every field is optional so a caller
 * sends only what it sets, and writable so a caller can build it field by field.
 */
type EmailMessageSearchFields = EmailMessageSearchData & {
  status: string | null;
  page: number;
  per_page: number;
  sort: 'created_at' | '-created_at' | 'subject' | '-subject' | 'status' | '-status';
};

export type EmailMessageSearchRequest = {
  -readonly [Key in keyof EmailMessageSearchFields]?: EmailMessageSearchFields[Key];
};

/** Runs one email history search; `null` keeps the query idle. */
export function useEmailMessageSearch(
  request: EmailMessageSearchRequest | null,
  options: {
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data: Diagonal.Notifications.Data.IndexEmailMessagesData | undefined };
        }) => number | false);
    keepPreviousData?: boolean;
  } = {}
) {
  const { keepPreviousData: keepData = true, ...queryOptions } = options;
  return useQuery<Diagonal.Notifications.Data.IndexEmailMessagesData>({
    queryKey: ['email-messages', 'search', request],
    queryFn: () => {
      const { status, ...body } = request ?? {};
      return axios
        .post('/api/email-messages/search', {
          ...body,
          ...(status ? { filter: { status } } : {})
        })
        .then((res) => res.data);
    },
    enabled: request !== null,
    placeholderData: request !== null && keepData ? keepPreviousData : undefined,
    ...queryOptions
  });
}

export function useEmailMessageDetail(emailMessageId: string | null) {
  return useQuery<Diagonal.Notifications.Data.ShowEmailMessageData>({
    queryKey: ['email-message-detail', emailMessageId],
    queryFn: () => axios.get(`/api/email-messages/${emailMessageId}`).then((res) => res.data),
    enabled: Boolean(emailMessageId)
  });
}

export function useEmailMessageBodyApi(emailMessageId: string | null, enabled: boolean) {
  return useQuery<string>({
    queryKey: ['email-message-body-api', emailMessageId],
    staleTime: Infinity,
    enabled: Boolean(emailMessageId) && enabled,
    queryFn: async () => {
      const response = await fetch(`/api/email-messages/${emailMessageId}/body`, {
        redirect: 'follow'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email message body');
      }

      return response.text();
    }
  });
}

/**
 * The short-lived URLs of a message's inline images, keyed by file id, for a
 * workspace page. See `useEmailMessageContentMedia` in `@Core/api/EmailMessageApi`.
 */
export function useEmailMessageContentMediaApi(emailMessageId: string | null, enabled: boolean) {
  return useEmailMessageContentMedia(
    emailMessageId && enabled ? `/api/email-messages/${emailMessageId}/content-media` : null
  );
}

export function useSendableEmailLayouts(enabled = true) {
  return useQuery<Diagonal.Notifications.Data.IndexSendableEmailLayoutsData>({
    queryKey: ['email-messages', 'layouts'],
    queryFn: () => axios.get('/api/email-messages/layouts').then((res) => res.data),
    enabled
  });
}

export function useSendableEmailLayout(emailLayoutId: string | null) {
  return useQuery<Diagonal.Notifications.Data.SendableEmailLayoutData>({
    queryKey: ['email-messages', 'layouts', emailLayoutId],
    queryFn: () =>
      axios.get(`/api/email-messages/layouts/${emailLayoutId}`).then((res) => res.data),
    enabled: Boolean(emailLayoutId)
  });
}

export type PreviewEmailPayload = {
  pre_resolved?: boolean;
  email_layout_id: string | null;
  subject: string | null;
  sample_data: Record<string, unknown>;
  render_values: boolean;
};

export function previewEmail(
  payload: PreviewEmailPayload
): Promise<Diagonal.Notifications.Data.EmailLayoutPreviewData> {
  return axios
    .post<Diagonal.Notifications.Data.EmailLayoutPreviewData>(
      '/api/email-messages/preview',
      payload
    )
    .then((response) => response.data);
}
